import { useState, useEffect, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, AlertTriangle, Timer, Boxes, MapPin, Menu, LogOut,
  ClipboardList, PackageOpen, ListChecks, Truck, ClipboardCheck,
  CalendarClock, Lock, ChevronRight, PackageSearch, Shield, Target,
} from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { EstoqarMark, EstoqarWordmark } from "@/components/Logo";
import { ROLE_ALLOWED } from "@/lib/roles";

const GROUPS = [
  {
    id: "gestao",
    title: "GESTÃO",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-link-dashboard", end: true },
      { to: "/ocorrencias", label: "Gestão de Ocorrências", icon: AlertTriangle, testid: "nav-link-ocorrencias" },
      { to: "/sla", label: "SLA de Expedição", icon: Timer, testid: "nav-link-sla" },
      { to: "/inventario", label: "Inventários & Auditoria", icon: Boxes, testid: "nav-link-inventario" },
    ],
  },
  {
    id: "operacao",
    title: "OPERAÇÃO",
    items: [
      { to: "/picklist", label: "Linha de Separação 1100", icon: ClipboardList, testid: "nav-link-picklist" },
      { to: "/reposicao", label: "Reposição Hub", icon: PackageOpen, testid: "nav-link-reposicao" },
      { to: "/enderecos", label: "Endereçamento", icon: MapPin, testid: "nav-link-enderecos" },
    ],
  },
  {
    id: "recebimento",
    title: "RECEBIMENTO DE MERCADORIA",
    items: [
      {
        to: "/recebimento",
        label: "Endereçamento Inteligente",
        icon: Truck,
        testid: "nav-link-recebimento",
      },
      { to: "/conferencia", label: "Conferência de Mercadoria", icon: ClipboardCheck, testid: "nav-link-conferencia" },
      { to: "/conferencia-volumes", label: "Conferência de Volumes", icon: PackageSearch, testid: "nav-link-conferencia-volumes" },
    ],
  },
];

const INDEPENDENT = [
  { to: "/separacao", label: "SKUs Alocados ER", icon: ListChecks, testid: "nav-link-separacao" },
  { to: "/validade", label: "13 Meses (Validade)", icon: CalendarClock, testid: "nav-link-validade" },
];

const LOGISTICA_ONLY = [
  { to: "/localizador", label: "Localizador Inteligente", icon: Target, testid: "nav-link-localizador" },
];

const LOGISTICA_EMAILS = ["logistica@boticam.com.br"];

function LockedItem({ n, onLocked }) {
  return (
    <button
      type="button"
      data-testid={`${n.testid}-locked`}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onLocked(n.label); }}
      title="Acesso bloqueado para o seu perfil"
      className="w-full flex items-center gap-3 text-sm rounded-md transition-colors text-slate-500 hover:text-slate-300 hover:bg-[#164A34]/30 pl-4 py-2.5 font-medium border-l-4 border-transparent cursor-not-allowed"
    >
      <n.icon className="w-4 h-4 shrink-0 opacity-60" />
      <span className="truncate opacity-70">{n.label}</span>
      <Lock className="w-3.5 h-3.5 ml-auto mr-2 text-amber-400/80" />
    </button>
  );
}

function ItemLink({ n, onNavigate }) {
  return (
    <NavLink
      to={n.to}
      end={n.end}
      data-testid={n.testid}
      onClick={onNavigate}
      className={({ isActive }) =>
        `flex items-center gap-3 text-sm rounded-md transition-colors ${
          isActive
            ? "bg-[#164A34] text-white font-semibold border-l-4 border-[#2DC26B] pl-3 py-2.5"
            : "text-slate-300 hover:text-white hover:bg-[#164A34]/60 pl-4 py-2.5 font-medium border-l-4 border-transparent"
        }`
      }
    >
      <n.icon className="w-4 h-4 shrink-0" />
      <span>{n.label}</span>
    </NavLink>
  );
}

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const allowed = ROLE_ALLOWED[user?.role];
  const isLocked = (to) => allowed && !allowed.includes(to);

  const groupContainsActive = (group) =>
    group.items.some((n) => n.end ? location.pathname === n.to : (location.pathname === n.to || location.pathname.startsWith(n.to + "/")));

  const initialOpen = useMemo(() => {
    const state = {};
    GROUPS.forEach((g) => { state[g.id] = groupContainsActive(g); });
    // if no group matches, expand GESTÃO by default
    if (!Object.values(state).some(Boolean)) state.gestao = true;
    return state;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [openMap, setOpenMap] = useState(initialOpen);

  // Auto-expand the group containing the current route on route change
  useEffect(() => {
    setOpenMap((prev) => {
      const next = { ...prev };
      GROUPS.forEach((g) => { if (groupContainsActive(g)) next[g.id] = true; });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLocked = (label) => {
    toast.info(`"${label}" ainda não liberada para o seu perfil`, {
      description: "Em breve o acesso será liberado.",
    });
  };

  const toggle = (id) => setOpenMap((m) => ({ ...m, [id]: !m[id] }));

  return (
    <div className="bg-[#0B1F17] text-slate-100 flex flex-col justify-between h-full border-r border-[#1A3A2C]">
      <div className="overflow-y-auto">
        <div className="p-5 border-b border-[#1A3A2C]/80 flex items-center gap-3">
          <EstoqarMark size={40} />
          <div>
            <EstoqarWordmark size="text-lg" />
            <div className="text-[9px] tracking-[0.18em] uppercase text-[#2DC26B]/80">Gestão de operações logísticas</div>
          </div>
        </div>
        <nav className="p-3 space-y-1">
          {GROUPS.map((group) => {
            const open = !!openMap[group.id];
            return (
              <div key={group.id} className="mb-1" data-testid={`nav-group-${group.id}`}>
                <button
                  type="button"
                  data-testid={`nav-group-toggle-${group.id}`}
                  aria-expanded={open}
                  onClick={() => toggle(group.id)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-[11px] font-bold tracking-[0.18em] uppercase text-[#2DC26B] hover:bg-[#164A34]/40 transition-colors"
                >
                  <span className="truncate">{group.title}</span>
                  <ChevronRight
                    className={`w-4 h-4 shrink-0 transition-transform duration-200 ${open ? "rotate-90" : "rotate-0"}`}
                  />
                </button>
                <div
                  className={`grid transition-all duration-200 ease-in-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                >
                  <div className="overflow-hidden">
                    <div className="pt-1 pl-2 space-y-1" data-testid={`nav-group-items-${group.id}`}>
                      {group.items.map((n) =>
                        isLocked(n.to)
                          ? <LockedItem key={n.to} n={n} onLocked={handleLocked} />
                          : <ItemLink key={n.to} n={n} onNavigate={onNavigate} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="pt-2 mt-2 border-t border-[#1A3A2C]/60 space-y-1">
            {INDEPENDENT.map((n) =>
              isLocked(n.to)
                ? <LockedItem key={n.to} n={n} onLocked={handleLocked} />
                : <ItemLink key={n.to} n={n} onNavigate={onNavigate} />
            )}
            {LOGISTICA_EMAILS.includes(user?.email) && LOGISTICA_ONLY.map((n) => (
              <ItemLink key={n.to} n={n} onNavigate={onNavigate} />
            ))}
          </div>
        </nav>
      </div>
      <div className="p-4 border-t border-[#1A3A2C] bg-[#06150F]/80 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white truncate" data-testid="sidebar-user-name">{user?.nome}</div>
          <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
        </div>
        <button
          data-testid="logout-button"
          onClick={logout}
          title="Sair"
          className="p-2 rounded-md text-slate-300 hover:text-white hover:bg-[#164A34] transition-colors"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isInventario = location.pathname.startsWith("/inventario");

  return (
    <div className="min-h-screen bg-white">
      {/* Desktop sidebar */}
      <aside className="hidden md:block fixed inset-y-0 left-0 w-64 z-40" data-testid="sidebar-desktop">
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden bg-white border-b border-[#D9E3DC] px-4 h-16 flex items-center gap-3 sticky top-0 z-30 shadow-sm">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="hamburger-menu-button" className="p-2 rounded-md border border-[#D9E3DC] text-[#0F2A1F]">
              <Menu className="w-5 h-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 border-none">
            <SidebarContent onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="flex items-center gap-2">
          <EstoqarMark size={28} boxColor="#0B1F17" boxStroke="#2DC26B" />
          <EstoqarWordmark light={false} size="text-base" />
        </div>
      </header>

      <main className={`md:pl-64 ${isInventario ? "bg-white" : ""}`} data-testid="main-content">
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto">{children}</div>
      </main>
    </div>
  );
}
